<script>
  import { onMount } from 'svelte'
  import { getOpsAudit, getOpsJournal } from '../../api.js'
  import { auditData, journalData } from '../../stores.js'
  import {
    SITE_MODE,
    opsUnitsForSite,
    defaultOpsUnitForSite,
  } from '../../lib/hostSite.js'
  import {
    readJournalUnit,
    writeJournalUnit,
    horizonForUnit,
  } from '../../lib/horizon.js'

  const JOURNAL_UNITS = opsUnitsForSite(SITE_MODE)
  const showUnitSwitch = JOURNAL_UNITS.length > 1
  const auditHint =
    SITE_MODE === 'live'
      ? 'Последние start / stop / restart · tail ≤ 50 · pmbot-live-15m'
      : 'Последние start / stop / restart · tail ≤ 50 · paper units'

  let auditBusy = false
  let journalBusy = false
  let journalUnit = (() => {
    const saved = readJournalUnit(JOURNAL_UNITS)
    return JOURNAL_UNITS.includes(saved) ? saved : defaultOpsUnitForSite(SITE_MODE)
  })()

  async function loadAudit() {
    auditBusy = true
    try {
      const { res, data } = await getOpsAudit(50)
      if (res.ok && data) auditData.set(data)
    } finally {
      auditBusy = false
    }
  }

  async function loadJournal() {
    journalBusy = true
    try {
      const { res, data } = await getOpsJournal({ unit: journalUnit, lines: 40 })
      if (res.ok && data) journalData.set(data)
    } finally {
      journalBusy = false
    }
  }

  function selectJournalUnit(u) {
    journalUnit = u
    writeJournalUnit(u, JOURNAL_UNITS)
    loadJournal()
  }

  onMount(() => {
    loadAudit()
    loadJournal()
  })

  $: entries = (
    ($auditData && ($auditData.entries || $auditData.items || $auditData.lines)) || []
  ).filter((e) => {
    if (!e || typeof e !== 'object') return false
    const u = e.unit
    if (u == null || u === '') return true
    return JOURNAL_UNITS.includes(String(u))
  })
  $: journalLines = ($journalData && $journalData.lines) || []
</script>

<div class="pm-logs-grid">
  <section class="pm-surface" aria-label="Ops audit">
    <div class="pm-section-head">
      <h2>Audit</h2>
      <button type="button" class="pm-btn" disabled={auditBusy} on:click={loadAudit}>
        {auditBusy ? '…' : 'Обновить'}
      </button>
    </div>
    <p class="pm-meta">{auditHint}</p>
    <ul class="pm-feed-list">
      {#if !entries.length}
        <li class="pm-empty">Нет записей audit (или недоступно локально).</li>
      {:else}
        {#each entries as e, i}
          <li class="pm-feed-item" style="grid-template-columns:1fr">
            <div>
              <strong>{e.action || e.event || 'ops'}</strong>
              {e.unit ? ' · ' + e.unit : ''}
              {e.user ? ' · ' + e.user : ''}
              <div class="pm-meta">{e.ts_utc || e.ts || e.time || ''}</div>
              {#if e.ok === false}
                <div class="pm-ops-msg--bad">{e.error || 'fail'}</div>
              {/if}
            </div>
          </li>
        {/each}
      {/if}
    </ul>
  </section>

  <section class="pm-surface" aria-label="Ops journal">
    <div class="pm-section-head">
      <h2>Journal</h2>
      <button type="button" class="pm-btn" disabled={journalBusy} on:click={loadJournal}>
        {journalBusy ? '…' : 'Обновить'}
      </button>
    </div>
    {#if showUnitSwitch}
      <div class="pm-hz" role="group" aria-label="Journal unit">
        {#each JOURNAL_UNITS as u}
          <button
            type="button"
            class="pm-hz__btn"
            class:is-on={journalUnit === u}
            on:click={() => selectJournalUnit(u)}
          >
            {horizonForUnit(u)}
          </button>
        {/each}
      </div>
    {/if}
    <p class="pm-meta">
      journalctl · {($journalData && $journalData.unit) || journalUnit} · redacted
    </p>
    <pre class="pm-pre">{journalLines.length ? journalLines.join('\n') : 'Нет строк journal.'}</pre>
  </section>
</div>

<style>
  .pm-hz {
    margin: 0.35rem 0 0.55rem;
  }
</style>
